import React, { useState } from 'react';
import { Container, Row, Col, Form } from 'react-bootstrap';
import { Send, MapPin, Clock, Headphones, MessageSquare } from 'lucide-react';
import Button from '../../../components/common/Button/MyButton';
import '../../../assets/styles/customer/layout/ContactFormSection.css';

const ContactFormSection = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
  };

  return (
    <section className="contact-form-section">
      
      {/* Floating Communication Icons */}
      <div className="form-floating-icons">
        <div className="float-form-icon icon-1">📱</div>
        <div className="float-form-icon icon-2">✉️</div>
        <div className="float-form-icon icon-3">💬</div>
        <div className="float-form-icon icon-4">📞</div>
        <div className="float-form-icon icon-5">📧</div>
        <div className="float-form-icon icon-6">🎧</div>
      </div>

      {/* Decorative Circles */}
      <div className="form-circles">
        <div className="form-circle circle-1"></div>
        <div className="form-circle circle-2"></div>
        <div className="form-circle circle-3"></div>
      </div>

      {/* Dot Pattern */}
      <div className="form-dot-pattern">
        <div className="form-dot"></div>
        <div className="form-dot"></div>
        <div className="form-dot"></div>
        <div className="form-dot"></div>
        <div className="form-dot"></div>
      </div>

      <Container>
        <Row className="g-4">
          
          {/* Left Side - Contact Form */}
          <Col lg={6} md={12}>
            <div className="contact-form-wrapper">
              
              <div className="form-header">
                <h2 className="form-title">
                  Send Us a <span className="form-title-highlight">Message</span>
                </h2>
                <p className="form-subtitle">
                  Fill out the form and we'll get back to you within 24 hours.
                </p>
              </div>

              <Form onSubmit={handleSubmit} className="contact-form">
                
                {/* Full Name & Email Row */}
                <Row className="g-3 mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="form-label">Full Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="fullName"
                        placeholder="Your name"
                        value={formData.fullName}
                        onChange={handleChange}
                        className="form-input"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="form-label">Email</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={handleChange}
                        className="form-input"
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* Phone & Subject Row */}
                <Row className="g-3 mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="form-label">Phone (Optional)</Form.Label>
                      <Form.Control
                        type="tel"
                        name="phone"
                        placeholder="+92 300 1234567"
                        value={formData.phone}
                        onChange={handleChange}
                        className="form-input"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="form-label">Subject</Form.Label>
                      <Form.Control
                        type="text"
                        name="subject"
                        placeholder="How can we help?"
                        value={formData.subject}
                        onChange={handleChange}
                        className="form-input"
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* Message */}
                <Form.Group className="mb-4">
                  <Form.Label className="form-label">Message</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    name="message"
                    placeholder="Tell us more about your inquiry..."
                    value={formData.message}
                    onChange={handleChange}
                    className="form-textarea"
                    required
                  />
                </Form.Group>

                {/* Submit Button */}
                <Button 
                  variant="primary" 
                  type="submit" 
                  className="form-submit-btn"
                >
                  <Send size={18} className="me-2" />
                  Send Message
                </Button>

              </Form>

            </div>
          </Col>

          {/* Right Side - Info Cards */}
          <Col lg={6} md={12}>
            <div className="contact-info-wrapper">
              
              {/* Office Address Card */}
              <div className="info-card office-card">
                <div className="info-icon-wrapper">
                  <MapPin size={24} strokeWidth={2} />
                </div>
                <h3 className="info-card-title">Our Office</h3>
                <div className="info-card-content">
                  <p className="office-name">MilkFlow Technologies</p>
                  <p className="office-address">123 Tech Park, Gulberg III</p>
                  <p className="office-city">Lahore, Pakistan 54000</p>
                </div>
              </div>

              {/* Business Hours Card */}
              <div className="info-card hours-card">
                <div className="info-icon-wrapper">
                  <Clock size={24} strokeWidth={2} />
                </div>
                <h3 className="info-card-title">Business Hours</h3>
                <div className="info-card-content">
                  <div className="hours-row">
                    <span className="hours-day">Monday - Friday</span>
                    <span className="hours-time">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="hours-row">
                    <span className="hours-day">Saturday</span>
                    <span className="hours-time">10:00 AM - 4:00 PM</span>
                  </div>
                  <div className="hours-row">
                    <span className="hours-day">Sunday</span>
                    <span className="hours-time-closed">Closed</span>
                  </div>
                </div>
              </div>

              {/* Urgent Help Card */}
              <div className="info-card urgent-card">
                <div className="urgent-icon-wrapper">
                  <Headphones size={40} strokeWidth={2} />
                </div>
                <h3 className="urgent-title">Need Urgent Help?</h3>
                <p className="urgent-text">
                  For urgent matters, reach us directly via WhatsApp for instant support.
                </p>
                <Button variant="success" className="whatsapp-btn">
                  <MessageSquare size={18} className="me-2" />
                  WhatsApp Us
                </Button>
              </div>

            </div>
          </Col>

        </Row>
      </Container>
    </section>
  );
};

export default ContactFormSection;